// ─── lireTextePdf.js ─────────────────────────────────────────────────
// Extraire le texte d'un PDF dans le navigateur.
//
// Sert a relire la DEMANDE D'ANALYSES que MGD renvoie apres une
// prescription. Ce document ne porte aucun resultat, aucune valeur
// biologique : seulement une liste de codes d'analyses et le nom de la
// cliente. On le lit et on le jette, rien n'est stocke, rien ne part au
// serveur. C'est ce qui permet de rester loin de la machinerie d'ingestion
// des rapports, qui elle manipule des donnees cliniques et porte quotas,
// instantanes et journaux immuables.
//
// pdfjs pese pres d'un megaoctet. Il est charge a la demande, au moment ou
// Anissa depose un fichier, jamais a l'ouverture du cockpit.

/** Au-dela, on refuse : une demande MGD fait quelques dizaines de kilo-octets. */
export const TAILLE_MAX_OCTETS = 8 * 1024 * 1024;

/** Une demande tient sur une page ; on borne quand meme. */
export const PAGES_MAX = 10;

export class LecturePdfError extends Error {
  constructor(raison) {
    super(raison);
    this.name = 'LecturePdfError';
    this.raison = raison;
  }
}

/**
 * Chargement paresseux, remplacable dans les tests.
 *
 * ⚠ ON CHARGE LA VERSION « LEGACY », PAS LA MODERNE.
 *
 * 25.08.2026 — Le premier essai reel a echoue chez Anissa, sur Mac, avec un
 * « .find is not a function » venu des entrailles de pdfjs. Le meme fichier
 * passait chez nous : en developpement, en build de production, worker et
 * polices servis. La seule variable restante etait son navigateur.
 *
 * pdfjs publie deux builds. Le moderne suppose un navigateur a jour ; le
 * legacy est fait pour les autres, Safari compris, et c'est celui qui
 * passait dans nos essais hors navigateur. On prend le legacy : lire un PDF
 * de laboratoire n'a aucun besoin des dernieres syntaxes, et un cockpit qui
 * marche partout vaut mieux qu'un cockpit rapide qui marche ici.
 *
 * ⚠ Les deux imports doivent venir du MEME build. Melanger un module moderne
 * avec un worker legacy, ou l'inverse, produit des erreurs incomprehensibles
 * du meme genre.
 */
async function chargerPdfjs() {
  const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs');
  // Vite sert le worker comme asset ; sans lui pdfjs bloque le fil principal
  // ou echoue selon les versions.
  const { default: workerUrl } = await import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?url');
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;
  return pdfjs;
}

/**
 * Rend le texte brut d'un PDF, pages concatenees.
 *
 * L'ordre des fragments suit celui du document, qui sur une mise en page en
 * colonnes n'est pas l'ordre de lecture. Les appelants ne doivent pas s'y
 * fier : `demandeMgd.js` travaille sur des jetons, pas sur des lignes.
 *
 * @param {File|Blob|ArrayBuffer} fichier
 * @param {{chargerPdfjs?: Function}} [deps]
 * @returns {Promise<string>}
 * @throws {LecturePdfError} 'fichier_absent' | 'trop_volumineux' | 'illisible'
 */
export async function lireTextePdf(fichier, deps = {}) {
  if (!fichier) throw new LecturePdfError('fichier_absent');

  const taille = typeof fichier.size === 'number' ? fichier.size : fichier.byteLength;
  if (Number.isFinite(taille) && taille > TAILLE_MAX_OCTETS) {
    throw new LecturePdfError('trop_volumineux');
  }

  let octets;
  try {
    octets = fichier instanceof ArrayBuffer ? fichier : await fichier.arrayBuffer();
  } catch {
    throw new LecturePdfError('illisible');
  }

  const charger = deps.chargerPdfjs || chargerPdfjs;

  // ⚠ 25.08.2026 — CHAQUE ETAPE PORTE SON NOM.
  //
  // Le premier echec reel, sur le Safari d'Anissa, a rendu « .find is not a
  // function » sans dire ou. J'ai suppose deux fois — les polices, puis le
  // build moderne — et je me suis trompe deux fois, en lui faisant refaire
  // le geste a chaque coup. Un message qui nomme l'etape et joint la
  // premiere ligne de pile aurait tranche du premier essai.
  //
  // Instrumenter coute une ligne par etape. Deviner coute un aller-retour
  // avec quelqu'un qui travaille.
  let etape = 'chargement de pdfjs';
  try {
    const pdfjs = await charger();
    etape = 'ouverture du document';
    const doc = await pdfjs.getDocument({
      data: new Uint8Array(octets),
      // Un PDF de labo n'a aucune raison d'aller chercher quoi que ce soit
      // au-dehors, ni d'executer du script.
      isEvalSupported: false,
      disableAutoFetch: true,
      enableXfa: false,
      // ⚠ 25.08.2026 — SANS CECI, LA DEMANDE MGD EST ILLISIBLE.
      //
      // Le PDF du portail n'incorpore pas ses polices : il declare des
      // polices standard et laisse le lecteur les fournir. pdfjs reclame
      // alors `standardFontDataUrl` et echoue sans. Le premier essai reel,
      // sur la demande de Guillemette, a rendu « je n'arrive pas a lire ce
      // PDF » sur un fichier parfaitement valide.
      //
      // Les 16 polices vivent dans `public/pdfjs/standard_fonts/`, servies
      // depuis notre propre domaine : rien n'est demande a un tiers, et le
      // fichier ne quitte toujours pas le navigateur.
      standardFontDataUrl: '/pdfjs/standard_fonts/',
    }).promise;

    const pages = Math.min(doc.numPages || 0, PAGES_MAX);
    const morceaux = [];
    for (let n = 1; n <= pages; n += 1) {
      etape = `page ${n}`;
      const page = await doc.getPage(n);
      etape = `texte de la page ${n}`;
      const contenu = await page.getTextContent();
      morceaux.push((contenu.items || []).map(i => i.str || '').join(' '));
    }
    etape = 'fermeture';
    await doc.destroy?.();

    const texte = morceaux.join('\n').trim();
    // Lecture reussie mais aucun caractere : la, et seulement la, on peut
    // affirmer qu'il n'y a pas de couche texte.
    if (!texte) throw new LecturePdfError('sans_texte');
    return texte;
  } catch (e) {
    if (e instanceof LecturePdfError) throw e;
    // ⚠ 25.08.2026 — ON NE DIAGNOSTIQUE PLUS A LA PLACE DU LECTEUR.
    //
    // Ce bloc rendait « si le PDF vient d'un scan, il ne contient pas de
    // texte ». Au premier essai reel la cause etait tout autre — des polices
    // standard non fournies — et le message a envoye Benoit chercher un
    // probleme de scan sur un fichier parfaitement valide. Une hypothese
    // affichee comme un diagnostic coute plus cher qu'un aveu d'ignorance.
    const echec = new LecturePdfError('echec_technique');
    // L'etape, le message, et la premiere ligne de pile utile. Ensemble ils
    // designent un endroit ; le message seul ne designe rien.
    const pile = String(e?.stack || '').split('\n')
      .map(l => l.trim()).filter(l => l && !l.startsWith('TypeError'))[0] || '';
    echec.detail = [
      etape,
      String(e?.message || e),
      pile.slice(0, 90),
    ].filter(Boolean).join(' | ').slice(0, 260);
    throw echec;
  }
}

/**
 * Message pour Anissa.
 *
 * Le detail technique n'est joint que sur un echec qu'on ne sait pas
 * expliquer : mieux vaut une ligne obscure qu'elle nous recopie qu'une
 * hypothese fausse presentee comme une certitude.
 */
export function messageLecture(raison, detail = null) {
  switch (raison) {
    case 'fichier_absent':
      return 'Aucun fichier reçu.';
    case 'trop_volumineux':
      return 'Ce fichier est trop lourd pour être une demande d’analyses. Vérifie que c’est le bon document.';
    case 'sans_texte':
      return 'Ce PDF ne contient aucun texte, seulement une image. C’est le cas d’un document scanné ou photographié : '
        + 'télécharge-le plutôt depuis le portail MGD.';
    case 'echec_technique':
      return 'La lecture de ce PDF a échoué pour une raison technique'
        + (detail ? ' : ' + detail : '')
        + '. Le fichier est peut-être parfaitement valide. Signale-le plutôt que de chercher de ton côté.';
    default:
      return 'La lecture a échoué.';
  }
}
